import { jsx, jsxs } from 'react/jsx-runtime';
import { BubbleMenu, isTextSelection } from '@tiptap/react';
import { useState, useEffect, useMemo } from 'react';
import Toolbar from '../ui/Toolbar/Toolbar.js';
import { textFormattingGroup } from '../ui/Toolbar/toolbarConfig.js';
import { ToolbarItemType } from '../ui/Toolbar/types.js';
import SimilarItemsInlineButton from './SimilarItemsInlineButton.js';
import Tooltip from '@fabric-app/ui/components/Tooltip';
import { isNoop } from '@fabric-app/utils/guards';
import { useExpandedFdocContext } from '../../resource-detail/components/ExpandedFdoc/ExpandedFdocProvider.js';
import { SummarizeInlineButton } from '../../assistant/components/SummarizeInlineButton.js';
import { EMBED_RESOURCE_TYPE_NAME } from '../Extensions/EmbedResource/contants.js';
import { useClientLayoutEffect } from '@fabric-app/hooks/useClientLayoutEffect';
import { createGlobalStyle, styled } from 'styled-components';

const InlineToolbar = ({ editor, contentRef }) => {
  const { onGetSimilarItemsToText, onSummarizeText } = useExpandedFdocContext();
  const [selectedText, setSelectedText] = useState(null);
  const [bubbleRef, setBubbleRef] = useState(null);
  useEffect(() => {
    if (!editor || isNoop(isNoop)) return;
    const onSelectionChange = () => {
      const selection = editor.state.selection;
      if (selection) {
        const text = editor.state.doc.textBetween(selection.from, selection.to, "\n");
        setSelectedText(text);
      } else {
        setSelectedText(null);
      }
    };
    editor?.on("selectionUpdate", onSelectionChange);
    return () => {
      editor?.off("selectionUpdate", onSelectionChange);
    };
  }, [
    editor,
    onGetSimilarItemsToText
  ]);
  const isSimilarDisabled = useMemo(() => {
    if (!selectedText) return "No text selected.";
    return selectedText.length > 1e3 ? "This highlight is too long, please highlight less than 1000 characters." : void 0;
  }, [
    selectedText
  ]);
  const aiGroup = useMemo(() => !isNoop(onGetSimilarItemsToText) && !isNoop(onSummarizeText) && !!selectedText ? {
    name: "AI",
    label: "AI",
    items: [
      {
        type: ToolbarItemType.Custom,
        key: "similar",
        render: () => /* @__PURE__ */ jsx(Tooltip, {
          label: isSimilarDisabled ? isSimilarDisabled : "See similar items",
          children: /* @__PURE__ */ jsx("div", {
            children: /* @__PURE__ */ jsx(SimilarItemsInlineButton, {
              onClick: () => onGetSimilarItemsToText?.(selectedText),
              onPointerDown: (e) => e.preventDefault(),
              style: {
                marginLeft: 6
              },
              disabled: !!isSimilarDisabled
            })
          })
        }, "similar")
      },
      {
        type: ToolbarItemType.Custom,
        key: "summarize",
        render: () => /* @__PURE__ */ jsx(Tooltip, {
          label: "Summarize text",
          children: /* @__PURE__ */ jsx(SummarizeInlineButton, {
            onClick: () => onSummarizeText?.(selectedText),
            style: {
              marginLeft: 2
            },
            onPointerDown: (e) => e.preventDefault()
          })
        }, "summarize")
      }
    ]
  } : void 0, [
    isSimilarDisabled,
    onGetSimilarItemsToText,
    onSummarizeText,
    selectedText
  ]);
  const toolbarGroups = useMemo(() => [
    textFormattingGroup,
    ...aiGroup ? [
      aiGroup
    ] : []
  ], [
    aiGroup
  ]);
  const shouldShow = ({ view, state, from, to, editor: editor2 }) => {
    if (editor2.isActive("embed") || editor2.isActive(EMBED_RESOURCE_TYPE_NAME)) return false;
    const { doc, selection } = state;
    const { empty } = selection;
    const isEmptyTextBlock = !doc.textBetween(from, to).length && isTextSelection(state.selection);
    const isChildOfMenu = bubbleRef?.contains(document.activeElement);
    const hasEditorFocus = view.hasFocus() || isChildOfMenu;
    if (!hasEditorFocus || empty || isEmptyTextBlock || !editor2.isEditable) {
      return false;
    }
    return true;
  };
  const [isMouseDown, setIsMouseDown] = useState(false);
  useClientLayoutEffect(() => {
    const dom = editor.view.dom;
    if (!dom) return;
    const handleMouseDown = () => {
      setIsMouseDown(true);
    };
    const handleMouseUp = () => {
      setIsMouseDown(false);
    };
    dom.addEventListener("mousedown", handleMouseDown);
    dom.addEventListener("mouseup", handleMouseUp);
    document.addEventListener("mouseup", handleMouseUp);
    document.addEventListener("blur", handleMouseUp);
    document.addEventListener("focusout", handleMouseUp);
    return () => {
      dom.removeEventListener("mousedown", handleMouseDown);
      dom.removeEventListener("mouseup", handleMouseUp);
      document.removeEventListener("mouseup", handleMouseUp);
      document.removeEventListener("blur", handleMouseUp);
      document.removeEventListener("focusout", handleMouseUp);
    };
  }, [
    editor
  ]);
  return /* @__PURE__ */ jsxs(BubbleWrapper, {
    ref: setBubbleRef,
    children: [
      /* @__PURE__ */ jsx(TippyRootPointerEvents, {}),
      /* @__PURE__ */ jsx(BubbleMenu, {
        className: "tiptap-inline-toolbar",
        editor,
        shouldShow,
        tippyOptions: {
          duration: 0,
          delay: 250,
          popperOptions: {
            modifiers: [
              {
                name: "offset",
                enabled: true,
                options: {
                  offset: [
                    0,
                    10
                  ]
                }
              },
              {
                name: "flip",
                enabled: true,
                options: {
                  fallbackPlacements: [
                    "top",
                    "bottom"
                  ],
                  boundary: contentRef ?? void 0
                }
              }
            ]
          }
        },
        children: /* @__PURE__ */ jsx(ToolbarWrapper, {
          "data-is-mouse-down": isMouseDown,
          "data-inline-toolbar-wrapper": true,
          children: /* @__PURE__ */ jsx(Toolbar, {
            editor,
            groups: toolbarGroups,
            floating: true,
            noSeperator: true
          })
        })
      })
    ]
  });
};
const TippyRootPointerEvents = createGlobalStyle([
  `[data-tippy-root]:has([data-inline-toolbar-wrapper][data-is-mouse-down='true']){pointer-events:none;z-index:-1 !important;}`
]);
const ToolbarWrapper = styled.div.withConfig({
  displayName: "InlineToolbar__ToolbarWrapper",
  componentId: "sc-22bf46ce-0"
})([
  `transition:opacity 0.2s ease-in-out;opacity:1;&[data-is-mouse-down='true']{pointer-events:none;opacity:0;position:absolute;}`
]);
const BubbleWrapper = styled.div.withConfig({
  displayName: "InlineToolbar__BubbleWrapper",
  componentId: "sc-22bf46ce-1"
})([
  `position:absolute;`
]);

export { InlineToolbar as default };
//# sourceMappingURL=InlineToolbar.js.map
